import * as React from 'react'
import { FormattedNumber, FormattedMessage } from 'react-intl'
import { useIsKnownCurrency } from '../types'
import { pipe } from 'fp-ts/lib/function'
import { record, option, array } from 'fp-ts'
import { useUnitInfo } from '../redux'
import { useSelector } from 'react-redux'

type Props = {
  unit: string
  amount: number
}

const Amount = ({ amount, unit }: Props) => {
  const locale = useSelector(s => s.locale)
  const unitInfo = useUnitInfo(unit)
  const isKnownCurrency = useIsKnownCurrency(unitInfo.symbol)

  if (isKnownCurrency) {
    return (
      <FormattedNumber
        value={amount / unitInfo.divider}
        currency={unitInfo.symbol}
        style="currency"
        currencySign="standard"
        currencyDisplay="symbol"
        minimumFractionDigits={unitInfo.fractionDigits}
        maximumFractionDigits={unitInfo.fractionDigits}
      />
    )
  }

  const formatted = (
    <FormattedNumber
      value={amount / unitInfo.divider}
      minimumFractionDigits={unitInfo.fractionDigits}
      maximumFractionDigits={unitInfo.fractionDigits}
    />
  )

  return locale === 'fr' ? (
    <>
      {formatted} {unitInfo.symbol}
    </>
  ) : (
    <>
      {unitInfo.symbol} {formatted}
    </>
  )
}

export default Amount

export const OptionalAmount = ({
  unit,
  amount,
  map,
  errorMessage,
}: {
  unit?: string | null | undefined
  amount: Record<string, number>
  map?: (_: number) => number
  errorMessage?: JSX.Element
}) =>
  pipe(
    option.fromNullable(unit),
    option.chain(u =>
      pipe(
        record.lookup(u, amount),
        option.map(map ?? (x => x)),
        option.map(amount => ({ unit: u, amount }))
      )
    ),
    option.alt(() =>
      pipe(
        record.toArray(amount),
        array.head,
        option.map(([unit, amount]) => ({ unit, amount }))
      )
    ),
    option.fold(
      (): JSX.Element =>
        !!errorMessage ? errorMessage : <FormattedMessage id="notApplicable" />,
      p => <Amount {...p} />
    )
  )
